import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import * as echarts from 'echarts';

const ALL = 'All';

interface FilterDef {
  label: string;
  key: string;
  multi: boolean;
  /** Relative dropdown endpoint from the config; ConfigService prepends the
   *  environment's base URL (qa / dev) — same pattern as the other pages. */
  apiUrl?: string;
  /** Request payload for the dropdown endpoint, straight from the config. */
  payload?: any;
}

interface SeriesDef {
  name: string;
  valueField: string;
  color?: string;
}

/** One bar-group on the chart: rows sharing the chart widget's `groupBy`
 *  fields, with one averaged value per configured series. */
interface ComboRecord {
  combination: string;
  groupLabel: string;
  values: number[];
  extras: { label: string; value: number; prefix: string }[];
}

/**
 * RER Engine Pricing-Insights report — same page pattern (and exact styling)
 * as DeepDiveComponent (Pricing Action History), but fully rendered from a
 * dashboard-config JSON in the standard widget schema: `filters.content`
 * drives the dropdowns, and `pageContent` rows carry a chart widget and a
 * table widget (widgetId / type / title / apiUrl / dataKey / headerColumns /
 * chartConfig), like every other page's JSON. The route's `configFile` data
 * names the file, mirroring the dashboard routes.
 */
@Component({
  selector: 'app-engine-pricing-insights',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './engine-pricing-insights.component.html',
  styleUrl: './engine-pricing-insights.component.scss',
})
export class EnginePricingInsightsComponent implements OnInit, AfterViewInit, OnDestroy {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private configService = inject(ConfigService);
  private hostElement: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild('chart') chartEl!: ElementRef<HTMLDivElement>;
  private chart?: echarts.ECharts;
  private viewReady = false;

  /** The widget-schema dashboard config (dashboard-name / filters / pageContent). */
  config: any = null;
  /** All table rows from the table widget's apiUrl. */
  private records: any[] = [];
  /** Rows returned specifically for the chart widget. */
  private chartRecords: any[] = [];
  /** Chart rows derived from the CURRENT filtered rows (rebuilt on every filter change). */
  private comboRecords: ComboRecord[] = [];

  isLoading = true;

  /** Stable filter definitions built once when the dashboard config loads. */
  filters: FilterDef[] = [];
  /** Per-filter selections; single-select filters hold one entry, ["All"] = no filter. */
  selected: Record<string, string[]> = {};
  /** Per-filter options loaded from the cascading-filter endpoint. */
  private dropdownOptions: Record<string, string[]> = {};
  dropdownSearchTerms: Record<string, string> = {};
  openDropdownKey: string | null = null;

  // ── Widget-schema accessors ────────────────────────────────────────────────

  /** Every widget across all pageContent rows. */
  private get widgets(): any[] {
    const rows = this.config?.pageContent || [];
    return rows.flatMap((row: any) => row?.columns || []);
  }

  get chartWidget(): any {
    return this.widgets.find((w) => `${w?.type}`.includes('chart')) || null;
  }

  get tableWidget(): any {
    return this.widgets.find((w) => `${w?.type}`.includes('table')) || null;
  }

  /** Dropdowns come from the standard `filters.content` block (name/key). */
  private buildFilters(config: any): FilterDef[] {
    const content = config?.filters?.content || [];
    return content.map((f: any): FilterDef => ({
      label: f.name,
      key: f.key,
      multi: !!f.isMultiSelect,
      apiUrl: f.apiUrl,
      payload: f.payload,
    }));
  }

  get columns(): { key: string; label: string }[] {
    return this.tableWidget?.headerColumns || [];
  }

  get emptyMessage(): string {
    return this.tableWidget?.emptyStateMessage || 'No records found.';
  }

  get canDownloadDeepDive(): boolean {
    return !this.isLoading && this.filteredRows.length > 0;
  }

  private get seriesDefs(): SeriesDef[] {
    return this.chartWidget?.chartConfig?.series || [];
  }

  // ── Lifecycle / data ───────────────────────────────────────────────────────

  ngOnInit(): void {
    const configFile =
      this.route.snapshot.data['configFile'] || 'rer-engine-pricing-insights.json';

    this.http.get<any>(`assets/dashboard-config/${configFile}`).subscribe((config) => {
      this.config = config;
      this.filters = this.buildFilters(config);
      for (const f of this.filters) {
        this.selected[f.key] = [ALL];
        this.dropdownSearchTerms[f.key] = '';
      }
      this.loadDropdownOptions();
      this.loadData();
    });
  }

  /**
   * Fetches the report data for the CURRENT filter selection. Called on init
   * and again every time a filter changes, so the payload always reflects
   * the selected values (server-side filtering) rather than just filtering
   * the initial, unfiltered fetch on the client.
   */
  private loadData(): void {
    const dataWidget = this.tableWidget || this.chartWidget;
    // No data endpoint configured yet — render the page with no records.
    if (!dataWidget?.apiUrl) {
      this.records = [];
      this.chartRecords = [];
      this.isLoading = false;
      this.renderChart();
      return;
    }
    this.isLoading = true;
    // Relative apiUrl → ConfigService prepends the environment's base URL
    // (qa / dev), same as the other report pages. The request method and
    // base payload come straight from the widget config so they can be
    // changed in the JSON without touching this component.
    const method = `${dataWidget.method || 'get'}`.toLowerCase() === 'post' ? 'post' : 'get';
    const data = method === 'post' ? { rawPayload: this.buildDataPayload(dataWidget) } : {};
    this.configService.getApi(dataWidget.apiUrl, {}, data, method).subscribe({
      next: (res) => {
        const tableRows = this.extractWidgetRows(res, this.tableWidget);
        const chartRows = this.extractWidgetRows(res, this.chartWidget);
        this.records = this.applyFieldMap(tableRows, this.tableWidget?.fieldMap);
        this.chartRecords = this.applyFieldMap(chartRows, this.chartWidget?.fieldMap);
        this.isLoading = false;
        this.renderChart();
      },
      error: () => {
        this.records = [];
        this.chartRecords = [];
        this.isLoading = false;
        this.renderChart();
      },
    });
  }

  /** Resolve a widget's array from a nested service response. */
  private extractWidgetRows(response: any, widget: any): any[] {
    if (Array.isArray(response)) return response;

    const responsePath = `${widget?.responsePath || ''}`.trim();
    if (responsePath) {
      const value = responsePath.split('.').reduce(
        (current: any, segment: string) => current?.[segment],
        response,
      );
      return Array.isArray(value) ? value : [];
    }

    const dataKey = widget?.dataKey || 'tableRows';
    return Array.isArray(response?.[dataKey]) ? response[dataKey] : [];
  }

  /**
   * Keeps the widget workflow envelope and rebuilds `requestData` from the
   * current non-"All" selections using each filter's configured key.
   * Response field mapping is handled independently after the request.
   */
  private buildDataPayload(dataWidget: any): any {
    const payload: Record<string, any> = {
      ...(dataWidget?.payload ?? {}),
      requestData: {},
    };

    for (const f of this.filters) {
      const sel = this.selected[f.key] || [];
      if (sel.length === 0 || sel.includes(ALL)) continue;
      payload['requestData'][f.key] = sel.length === 1 ? sel[0] : sel;
    }
    return payload;
  }

  /**
   * Renames response fields per the widget's `fieldMap` (response key →
   * config key), so the API's `marketing_config` / `tech_config` /
   * `cert_level` line up with the keys the filters and headerColumns use.
   */
  private applyFieldMap(rows: any[], fieldMap?: Record<string, string>): any[] {
    if (!fieldMap || typeof fieldMap !== 'object') return rows;
    const entries = Object.entries(fieldMap);
    if (!entries.length) return rows;
    return rows.map((row) => {
      const mapped: any = { ...row };
      for (const [from, to] of entries) {
        if (from in mapped) {
          mapped[to] = mapped[from];
          delete mapped[from];
        }
      }
      return mapped;
    });
  }

  /** Table rows returned by the API for the current selection.
   *  This report is server-filtered; applying the dropdown selections again
   *  here can hide valid deep-dive rows whose returned attributes differ from
   *  the request criteria.
   */
  get filteredRows(): any[] {
    return this.records;
  }

  /**
   * One bar-category per filtered table row (no grouping/averaging), so the
   * chart always matches the Deep Dive table exactly — same rows, same
   * RER/ReCon price values. Categories are labeled by ESN so each bar can be
   * traced back to its table row.
   */
  private buildComboRecords(rows: any[]): ComboRecord[] {
    const chartConfig = this.chartWidget?.chartConfig || {};
    const groupBy: string[] = chartConfig.groupBy || [];
    const tooltipFields: any[] = chartConfig.tooltip?.fields || [];

    const num = (item: any, field: string): number => Number(item[field] ?? 0);

    return rows.map((row) => ({
      combination: `${row['esn'] ?? ''}`,
      groupLabel: groupBy.map((field) => `${row[field]}`).join(' / '),
      values: this.seriesDefs.map((s) => num(row, s.valueField)),
      extras: tooltipFields.map((f) => ({
        label: f.label || f.key,
        value: num(row, f.key),
        prefix: f.prefix || '',
      })),
    }));
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.chart = echarts.init(this.chartEl.nativeElement);
    this.renderChart();
    window.addEventListener('resize', this.handleResize);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
    this.chart?.dispose();
  }

  private handleResize = (): void => this.chart?.resize();

  // ── Dropdowns ──────────────────────────────────────────────────────────────

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openDropdownKey = null;
  }

  toggleDropdown(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openDropdownKey = this.openDropdownKey === key ? null : key;
    if (this.openDropdownKey === key) {
      setTimeout(() => this.focusDropdownSearch(key));
    }
  }

  private focusDropdownSearch(key: string): void {
    const inputs = this.hostElement.nativeElement.querySelectorAll<HTMLInputElement>(
      '[data-dropdown-search]',
    );
    Array.from(inputs).find((input) => input.dataset['dropdownSearch'] === key)?.focus();
  }

  isDropdownOpen(key: string): boolean {
    return this.openDropdownKey === key;
  }

  setDropdownSearchTerm(key: string, term: string): void {
    this.dropdownSearchTerms[key] = term ?? '';
  }

  /** Loads all eight dropdowns with one initial cascading-filter request. */
  private loadDropdownOptions(): void {
    const payload = {
      requestData: { initial_load: true },
      responseList: [],
      entity: 'default_entity',
      workflowId: 'default_workflow',
      endpoints: [
        { functionName: 'RERPricingInsightsCascadingFiltersService', version: '1.0' },
      ],
      version: '1.0',
    };

    this.configService
      .getApi('/spriced/platform', {}, { rawPayload: payload }, 'post')
      .subscribe({
        next: (res: any) => {
          const responseFilters = this.extractCascadingFilters(res);
          const responseKeys: Record<string, string> = {
            displacement_litres: 'displacement_liters',
            fr: 'fuel_rating_hp',
          };

          for (const filter of this.filters) {
            const responseKey = responseKeys[filter.key] ?? filter.key;
            this.dropdownOptions[filter.key] = this.normalizeOptionValues(
              responseFilters?.[responseKey],
            );
          }
        },
        error: () => {
          for (const filter of this.filters) {
            this.dropdownOptions[filter.key] = [];
          }
        },
      });
  }

  /** Finds the nested `rer_pricing_insights_filters[].filters` response object. */
  private extractCascadingFilters(value: any): Record<string, any> | null {
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.extractCascadingFilters(item);
        if (found) return found;
      }
      return null;
    }
    if (!value || typeof value !== 'object') return null;
    if (value.filters && typeof value.filters === 'object' && !Array.isArray(value.filters)) {
      return value.filters;
    }
    for (const item of Object.values(value)) {
      const found = this.extractCascadingFilters(item);
      if (found) return found;
    }
    return null;
  }

  private normalizeOptionValues(value: any): string[] {
    if (!Array.isArray(value)) return [];
    return Array.from(
      new Set(
        value
          .filter((option) => option != null && option !== '' && option !== ALL)
          .map((option) => String(option)),
      ),
    );
  }

  /** Options come from the dropdown endpoint; if a filter has no endpoint
   *  (or nothing loaded yet), fall back to the dataset's unique values. */
  getFilteredOptions(key: string): string[] {
    const loaded = this.dropdownOptions[key];
    const unique =
      loaded ?? Array.from(new Set(this.records.map((row) => `${row[key]}`))).sort();
    const options = [ALL, ...unique];
    const term = (this.dropdownSearchTerms[key] || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.toLowerCase().includes(term));
  }

  selectOption(filter: FilterDef, value: string): void {
    if (!filter.multi) {
      this.selected[filter.key] = [value];
      this.openDropdownKey = null;
      this.dropdownSearchTerms[filter.key] = '';
    } else if (value === ALL) {
      this.selected[filter.key] = [ALL];
    } else {
      // Toggle the value; picking a real value drops "All", emptying falls back to it.
      const current = (this.selected[filter.key] || []).filter((v) => v !== ALL);
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      this.selected[filter.key] = next.length ? next : [ALL];
    }
    this.loadData();
  }

  isOptionSelected(key: string, option: string): boolean {
    return (this.selected[key] || []).includes(option);
  }

  hasSelectedValues(filter: FilterDef): boolean {
    const values = this.selected[filter.key] || [];
    return values.length > 0 && !values.includes(ALL);
  }

  clearSelectedOptions(filter: FilterDef, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.selected[filter.key] = [ALL];
    this.dropdownSearchTerms[filter.key] = '';
    this.loadData();
  }

  /** Trigger text: "All" or the selected values, comma-separated. */
  getTriggerLabel(filter: FilterDef): string {
    const sel = this.selected[filter.key] || [];
    if (sel.length === 0 || sel.includes(ALL)) return ALL;
    return sel.map((value) => this.humanizeEntityLabel(value)).join(', ');
  }

  private humanizeEntityLabel(value: string): string {
    const normalized = String(value || '').trim();
    if (!normalized) return normalized;

    const knownLabels: Record<string, string> = {
      custom_rer_engine_pricing: 'RER Engine Pricing',
    };

    return knownLabels[normalized] || normalized.replace(/_/g, ' ');
  }

  // ── Chart ──────────────────────────────────────────────────────────────────

  downloadDeepDiveCsv(): void {
    const rows = this.filteredRows;
    const columns = this.columns;
    if (!rows.length || !columns.length) return;

    const csvRows = [
      columns.map((col) => this.escapeCsvValue(col.label)).join(','),
      ...rows.map((row) =>
        columns.map((col) => this.escapeCsvValue(row?.[col.key])).join(','),
      ),
    ];
    const csv = csvRows.join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'RER Pricing Action History Report.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: any): string {
    if (value == null) return '';
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  private renderChart(): void {
    if (!this.viewReady || !this.chart || !this.config) return;

    this.comboRecords = this.buildComboRecords(this.chartRecords);

    if (this.comboRecords.length === 0) {
      this.chart.clear();
      return;
    }

    const chartConfig = this.chartWidget?.chartConfig || {};
    const palette: string[] = this.config?.themeOverrides?.chart?.palette || [];
    const categories = this.comboRecords.map((r) => r.combination);
    const seriesDefs = this.seriesDefs;
    const showDataLabels = chartConfig.showDataLabels !== false;

    // Resize BEFORE setOption — resize() after it would kill the bar entry
    // animation (ECharts stops in-flight animations on resize).
    this.chart.resize();

    this.chart.setOption(
      {
        animation: true,
        animationDuration: 800,
        animationEasing: 'cubicOut',
        animationDurationUpdate: 500,
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            const rows = Array.isArray(params) ? params : [params];
            const record = this.comboRecords[rows[0]?.dataIndex];
            if (!record) return '';
            const extraLines = record.extras
              .map(
                (e) => `<li>${e.label}: <b>${e.prefix}${e.value.toLocaleString('en-US')}</b></li>`,
              )
              .join('');
            return `<b>${record.combination}</b> (${record.groupLabel})
            <ul style="margin:0;padding-left:16px;">
              ${extraLines}
            </ul>`;
          },
        },
        legend: {
          top: 0,
          data: seriesDefs.map((s) => s.name),
          textStyle: { color: '#374151', fontSize: 11 },
        },
        grid: { left: '3%', right: '4%', top: '14%', bottom: '6%', containLabel: true },
        xAxis: {
          type: 'category',
          data: categories,
          axisTick: { alignWithLabel: true },
          name: chartConfig.xAxisName || '',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: { color: '#374151', fontSize: 12 },
        },
        yAxis: { type: 'value', name: chartConfig.yAxisName || '' },
        series: seriesDefs.map((s, seriesIndex) => ({
          name: s.name,
          type: 'bar',
          data: this.comboRecords.map((r) => r.values[seriesIndex]),
          barMaxWidth: 46,
          itemStyle: { color: s.color || palette[seriesIndex % (palette.length || 1)] },
          label: {
            show: showDataLabels,
            position: 'top',
            color: '#374151',
            fontWeight: 'normal',
            fontSize: 11,
          },
        })),
      },
      true,
    );
  }
}
